import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Volume2 } from "lucide-react";
import { PRAYER_VOICES } from "@/hooks/prayer/usePrayerVoices";
import { PrayerFeatureCard } from "./features/PrayerFeatureCard";
import { useToast } from "@/hooks/use-toast";
import { motion, useScroll, useTransform } from "framer-motion";

const PrayerMinistrySection = () => {
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: headingRef,
    offset: ["start end", "center center"]
  });
  const subtextScrollYProgress = useScroll({
    target: subtextRef,
    offset: ["start end", "center center"]
  }).scrollYProgress;
  const scale = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const subtextScale = useTransform(subtextScrollYProgress, [0, 1], [0, 1]);
  const [playingAudio, setPlayingAudio] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const {
    toast
  } = useToast();
  const handlePlayPreview = (voiceId: string, previewUrl: string) => {
    if (playingAudio === voiceId) {
      // Stop current audio if it's playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Play the new audio
      setPlayingAudio(voiceId);
      audioRef.current = new Audio(previewUrl);
      audioRef.current.onended = () => setPlayingAudio(null);
      audioRef.current.play().catch(error => {
        console.error("Error playing audio:", error);
        setPlayingAudio(null);
        toast({
          title: "Error",
          description: "Could not play the audio preview",
          variant: "destructive"
        });
      });
    }
  };

  return <section className="bg-white py-24">
    <div className="container mx-auto px-4 max-w-7xl">
      {/* Header */}
      <div className="text-center mb-16">
        <motion.h1 ref={headingRef} className="text-7xl font-playfair mb-4" style={{
          scale
        }}>
          <span className="text-heaven-gold">PRAYER</span>
          <span className="text-[#1A1F2C]"> TOOLS</span>
        </motion.h1>
        <motion.h2 ref={subtextRef} className="text-3xl text-[#1A1F2C] font-playfair mb-4" style={{
          scale: subtextScale
        }}>
          Not sure what to say when you pray?
        </motion.h2>
        <motion.p ref={subtextRef} className="text-gray-600 max-w-3xl mx-auto mb-4" style={{
          scale: subtextScale
        }}>
          Discover powerful AI-powered tools designed to deepen your 
          understanding of God's Word and strengthen your faith journey.
        </motion.p>
        <motion.p ref={subtextRef} className="text-heaven-gold" style={{
          scale: subtextScale
        }}>
          - Discover some of the features of our prayer tool below -
        </motion.p>
      </div>

      <div className="grid gap-6 mb-12 relative max-w-full">
        <div>
          <PrayerFeatureCard 
            title="Voice Prayer Companions" 
            description="Choose from multiple AI voices to pray with you and over you, creating a sense of shared faith even in solitude." 
            image="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//new%20prayer.jpg"
            voices={PRAYER_VOICES}
            onPlayPreview={handlePlayPreview}
            playingAudio={playingAudio}
          />
        </div>
      </div>

      <div className="mb-12">
        <PrayerFeatureCard 
          title="Infinite Customization" 
          description="Input anything on your heart—from personal struggles to moments of gratitude—and receive a prayer tailored precisely to your needs." 
          image="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//new%20prayer%202.jpg" 
        />
      </div>

      <div>
        <PrayerFeatureCard 
          title="Scripture Integration" 
          description="Every prayer is enriched with relevant Bible verses, deepening your connection to God's word and promises." 
          image="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//new%20prayer%203.jpg" 
        />
      </div>

      {/* New "Try Prayer Tool" Button */}
      <div className="text-center mt-12">
        <Link to="/prayer-generator">
          <Button 
            variant="blue" 
            size="xl" 
            className="mx-auto hover:bg-[#3367d6] transition-colors duration-300 text-white"
          >
            Try Prayer Tool
          </Button>
        </Link>
      </div>
    </div>
  </section>;
};

export default PrayerMinistrySection;
